"use client"

import React, { useEffect, useState } from "react"
import { ChevronDown } from "lucide-react"

const capabilities = [
  {
    id: "what-we-do-software-development",
    title: "Software Development",
    content:
      "From concept to launch, we build scalable mobile and web applications. High-quality code with a focus on performance and security.",
    link: "/software_dev",
  },
  {
    id: "what-we-do-web-design",
    title: "Web Design",
    content:
      "Websites that don't just look good – they convert. Responsive, accessible, and built for exceptional user experiences.",
    link: "/webdesign",
  },
  {
    id: "what-we-do-logo-design",
    title: "Logo & Brand Design",
    content:
      "We craft visual identities that communicate your brand. From logo to full brand systems and marketing assets.",
    link: "/logo-design",
  },
  {
    id: "what-we-do-digital-marketing",
    title: "Digital Marketing & Ads — Coming soon",
    content:
      "Campaigns driven by data and creativity. We help you reach the right audience and grow with measurable performance.",
    link: "/#contact",
  },
]

export function Capabilities() {
  const [openIndex, setOpenIndex] = useState(0)
  const whatsappUrl = "https://wa.me/18763932065"

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace("#", "")
      const idx = capabilities.findIndex((c) => c.id === hash)
      if (idx !== -1) setOpenIndex(idx)
    }

    applyHash()
    window.addEventListener("hashchange", applyHash)
    return () => window.removeEventListener("hashchange", applyHash)
  }, [])

  return (
    <section
      id="what-we-do"
      style={{ background: "#0c0c10" }}
      className="py-16 sm:py-20 lg:py-28"
    >
      <style>{`
        .cap-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          padding: 4px 10px;
          border: 1px solid #2e2e38;
          border-radius: 999px;
          color: #666;
        }

        .cap-divider {
          width: 100%;
          height: 1px;
          background: #1e1e26;
        }

        .cap-row {
          border-bottom: 1px solid #1e1e26;
        }

        .cap-trigger {
          width: 100%;
          text-align: left;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 20px 0;
          background: none;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
        }

        .cap-trigger:hover .cap-title {
          color: #ff424d;
        }

        .cap-num {
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          font-weight: 400;
          color: #333340;
          font-size: 18px;
          flex-shrink: 0;
          line-height: 1;
          transition: color 0.3s;
        }

        .cap-num.active {
          color: #ff424d;
        }

        .cap-title {
          font-size: clamp(15px, 2vw, 18px);
          font-weight: 600;
          color: #e0ddd8;
          transition: color 0.2s;
          line-height: 1.3;
        }

        .cap-title.active {
          color: #ff424d;
        }

        .cap-chevron {
          flex-shrink: 0;
          color: #444;
          transition: transform 0.25s ease, color 0.2s;
        }

        .cap-chevron.open {
          transform: rotate(180deg);
          color: #ff424d;
        }

        .cap-body {
          padding: 0 0 20px 32px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .cap-learn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 13px;
          font-weight: 600;
          color: #ff424d;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          padding-bottom: 1px;
          transition: border-color 0.2s;
        }

        .cap-learn:hover {
          border-color: #ff424d;
        }

        .cap-book {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 700;
          color: #0c0c10;
          background: #f0ede8;
          border: 1px solid #2e2e38;
          border-radius: 999px;
          padding: 8px 12px;
          text-decoration: none;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .cap-book:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

      `}</style>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">

        {/* Header */}
        <div className="mb-12 lg:mb-16">
          <p className="cap-tag mb-5">What we do</p>
          <h2 className="max-w-2xl" style={{
            fontWeight: 700,
            fontSize: "clamp(28px, 4vw, 48px)",
            lineHeight: 1.15,
            color: "#f0ede8",
          }}>
            We don't do templates.{" "}
            <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontWeight: 400, color: "#b91c1c" }}>
              Every project starts from zero
            </span>{" "}
            and ends with impact.
          </h2>
        </div>

        <div className="cap-divider mb-0" />

        {/* Body: accordion */}
        <div className="grid grid-cols-1 items-start mt-6">

          {/* Accordion */}
          <div>
            {capabilities.map((cap, index) => {
              const isOpen = openIndex === index
              const num = String(index + 1).padStart(2, "0")
              return (
                <div key={cap.id} id={cap.id} className="cap-row">
                  <button
                    className="cap-trigger"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                      <span className={`cap-num ${isOpen ? "active" : ""}`}>{num}</span>
                      <span className={`cap-title ${isOpen ? "active" : ""}`}>{cap.title}</span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`cap-chevron ${isOpen ? "open" : ""}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="cap-body">
                      <p style={{ color: "#888", fontSize: "14px", lineHeight: 1.8, marginBottom: "14px", maxWidth: "480px" }}>
                        {cap.content}
                      </p>
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cap-book"
                      >
                        Book service
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M7 17L17 7M17 7H7M17 7v10" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

        </div>
      </div>
    </section>
  )
}